/**
 * Turn library ids into CSS and glyph descriptors.
 * No file access: pass the library document (or omit it for built-ins only).
 *
 * Public stylesheets never link to Google Fonts. Imported / uploaded faces use
 * opaque CSS family names; admin UI shows the human `label`.
 */

import { BUILTIN_FACES, DEFAULT_FACE_ID, DEFAULT_ICON_SET } from './builtins.js';
import { DEFAULT_ICON_SET_ID, FACE_ROLES, GLYPH_ROLES, uploadedFaceId } from './schema.js';
import { videoChromeFaceIds } from '../theme/video-chrome.js';

/**
 * Faces available to pick / show in Library: true built-ins plus anything
 * already hosted on this server. Google catalog stubs (import-to-host) are
 * omitted — add those via Library → Import Google Font.
 */
export function listFaces(library) {
    const builtins = BUILTIN_FACES.filter((face) => !face.google);
    return [...builtins, ...(library?.faces || [])];
}

export function listIconSets(library) {
    const sets = Array.isArray(library?.iconSets) ? library?.iconSets : [];
    if (sets.some((set) => set.id === DEFAULT_ICON_SET_ID)) return sets;
    return [DEFAULT_ICON_SET, ...sets];
}

/**
 * @param {string} id - Built-in id, `face:<id>`, or legacy `custom:<id>`
 * @param {Object} [library]
 */
export function resolveFace(id, library) {
    // Pickers once stored hosted imports by their bare library id.
    const bare = typeof id === 'string' && !BUILTIN_FACES.some((face) => face.id === id)
        && (library?.faces || []).some((face) => face.id === id);
    const uploaded = bare ? id : uploadedFaceId(id);
    if (uploaded) {
        const face = (library?.faces || []).find((entry) => entry.id === uploaded);
        if (face) {
            const family = faceFamily(face);
            const fallback = hasMetrics(face) ? `, '${family} Fallback'` : '';
            return {
                id: `face:${face.id}`,
                label: face.label || family,
                css: `'${family}'${fallback}, system-ui, sans-serif`,
                google: null,
                face,
            };
        }
    }

    const builtin = BUILTIN_FACES.find((face) => face.id === id)
        || BUILTIN_FACES.find((face) => face.id === DEFAULT_FACE_ID);

    // Built-ins that used to load from Google Fonts CDN no longer do on the
    // public site (privacy). Until the face is imported into the library,
    // fall back to system UI and do not emit the marketing family name.
    if (builtin.google) {
        return {
            id: builtin.id,
            label: builtin.label,
            css: 'system-ui, -apple-system, "Segoe UI", sans-serif',
            google: null,
            /** Admin picker may still preview via Google before import. */
            googlePreview: builtin.google,
            face: null,
            needsImport: true,
        };
    }

    return {
        id: builtin.id,
        label: builtin.label,
        css: builtin.css,
        google: null,
        face: null,
        preload: builtin.preload || null,
    };
}

/**
 * @param {string} setId
 * @param {string} roleId - A role id, or a catalog id (`builtin:close`, `g` + 8 hex)
 * @param {Object} [library]
 */
export function resolveGlyph(setId, roleId, library) {
    const catalog = catalogGlyph(roleId, library);
    if (catalog) return catalog;
    const sets = listIconSets(library);
    const set = sets.find((entry) => entry.id === (setId || DEFAULT_ICON_SET_ID)) || DEFAULT_ICON_SET;
    const role = GLYPH_ROLES.find((entry) => entry.id === roleId) || GLYPH_ROLES[0];
    const glyph = set.glyphs?.[role.id] || DEFAULT_ICON_SET.glyphs[role.id];
    return {
        role: role.id,
        label: glyph?.label || role.label,
        url: glyph?.url || null,
        builtin: glyph?.builtin || role.id,
    };
}

function catalogGlyph(id, library) {
    if (typeof id !== 'string') return null;
    const catalogId = id.startsWith('builtin:') || /^g[0-9a-f]{8}$/.test(id);
    if (!catalogId) return null;
    const glyphs = Array.isArray(library?.glyphs) ? library.glyphs : [];
    const glyph = glyphs.find((entry) => entry.id === id);
    if (!glyph) {
        if (!id.startsWith('builtin:')) return null;
        const roleId = id.slice('builtin:'.length);
        const role = GLYPH_ROLES.find((entry) => entry.id === roleId);
        if (!role) return null;
        return { role: role.id, label: role.label, url: null, builtin: role.id };
    }
    return {
        role: id,
        label: glyph.name || glyph.label || id,
        url: glyph.url || null,
        builtin: glyph.builtin || null,
    };
}

function faceVariants(face) {
    if (Array.isArray(face?.variants) && face.variants.length) return face.variants;
    if (face?.url) {
        return [{
            url: face.url,
            format: face.format || 'woff2',
            weight: face.weight || 400,
            style: face.style || 'normal',
        }];
    }
    return [];
}

function faceFamily(face) {
    return face.family || `f${face.id}`;
}

function hasMetrics(face) {
    return Number(face?.metrics?.sizeAdjust) > 0;
}

/**
 * One rule per file, style and unicode-range: a variable font that serves
 * several weights from one file gets a `font-weight` range instead of a rule
 * per weight.
 */
function variantRules(face) {
    const groups = new Map();
    faceVariants(face).forEach((variant) => {
        const style = variant.style || 'normal';
        const key = `${variant.url}|${style}|${variant.unicodeRange || ''}`;
        const weight = Number(variant.weight) || 400;
        const group = groups.get(key);
        if (group) group.weights.push(weight);
        else groups.set(key, { ...variant, style, weights: [weight] });
    });
    return [...groups.values()].map((group) => {
        const min = Math.min(...group.weights);
        const max = Math.max(...group.weights);
        return { ...group, weight: min === max ? `${min}` : `${min} ${max}` };
    });
}

function fallbackFaceCss(face) {
    const { sizeAdjust, ascentOverride, descentOverride, lineGapOverride } = face.metrics;
    const parts = [`font-family:'${faceFamily(face)} Fallback'`, "src:local('Arial')", `size-adjust:${sizeAdjust}%`];
    if (Number.isFinite(ascentOverride)) parts.push(`ascent-override:${ascentOverride}%`);
    if (Number.isFinite(descentOverride)) parts.push(`descent-override:${descentOverride}%`);
    if (Number.isFinite(lineGapOverride)) parts.push(`line-gap-override:${lineGapOverride}%`);
    return `@font-face{${parts.join(';')};}`;
}

/**
 * Faces with stored metrics swap in over a resized Arial fallback; faces
 * without them keep `block` so text does not jump when the file arrives.
 */
export function fontFaceCss(faces) {
    return (faces || []).flatMap((face) => {
        const family = faceFamily(face);
        const metrics = hasMetrics(face);
        const display = metrics ? 'swap' : 'block';
        const rules = variantRules(face).map((variant) => {
            const range = variant.unicodeRange ? `unicode-range:${variant.unicodeRange};` : '';
            return `@font-face{font-family:'${family}';src:url('${variant.url}') format('${variant.format || 'woff2'}');font-weight:${variant.weight};font-style:${variant.style};font-display:${display};${range}}`;
        });
        return metrics ? [...rules, fallbackFaceCss(face)] : rules;
    }).join('');
}

/**
 * Font custom properties and @font-face text for one theme.
 * Never returns a Google Fonts stylesheet URL — fonts are self-hosted only.
 * @param {Object} theme
 * @param {Object} [library]
 */
function withThemeFaces(theme, library) {
    const faces = [...(library?.faces || [])];
    (theme?.brand?.customFonts || []).forEach((font) => {
        if (font?.id && !faces.some((face) => face.id === font.id)) faces.push(font);
    });
    return { faces, iconSets: library?.iconSets || [] };
}

/** Face ids referenced by typed header blocks (Brand / Menu / MenuToggle drawer). */
function faceIdsFromHeaderBlocks(theme) {
    const ids = [];
    const blocks = theme?.header?.blocks;
    if (!Array.isArray(blocks)) return ids;
    blocks.forEach((block) => {
        [block?.props, block?.deviceProps?.tablet, block?.deviceProps?.phone].forEach((props) => {
            if (props?.faceId) ids.push(props.faceId);
            if (props?.drawerFaceId) ids.push(props.drawerFaceId);
        });
    });
    return ids;
}

export function cssForTheme(theme, library) {
    const lib = withThemeFaces(theme, library);
    const vars = {};
    const faces = [];

    FACE_ROLES.forEach((role) => {
        if (role.optional && !theme?.fonts?.[role.id]) return;
        const resolved = resolveFace(theme?.fonts?.[role.id], lib);
        vars[role.cssVar] = resolved.css;
        if (resolved.face && !faces.some((face) => face.id === resolved.face.id)) faces.push(resolved.face);
    });

    // Header block faces are not always dual-written onto fonts.* (e.g. drawer
    // font). Still emit @font-face so var(--font-face-*) / inline families work.
    [...faceIdsFromHeaderBlocks(theme), ...videoChromeFaceIds(theme)].forEach((id) => {
        const resolved = resolveFace(id, lib);
        if (resolved.face && !faces.some((face) => face.id === resolved.face.id)) {
            faces.push(resolved.face);
        }
    });

    // Page text can set any library face inline, so every one gets a rule. A
    // browser only downloads a face's files once something on the page uses it.
    const allFaces = [...faces, ...lib.faces.filter((face) => face?.id && !faces.some((used) => used.id === face.id))];

    return {
        vars,
        faces,
        fontFaceCss: fontFaceCss(allFaces),
        /** @deprecated Always null — public site never loads fonts.googleapis.com */
        googleHref: null,
    };
}

/** The file a face's first paint needs: its latin regular (or only) file. */
function firstPaintFile(resolved) {
    if (!resolved.face) return resolved.preload ? { url: resolved.preload, format: 'woff2' } : null;
    const variants = faceVariants(resolved.face);
    const latin = variants.filter((variant) => !variant.subset || variant.subset === 'latin');
    const pool = latin.length ? latin : variants;
    return pool.find((variant) => variant.url === resolved.face.url)
        || pool.find((variant) => Number(variant.weight) === 400 && (variant.style || 'normal') === 'normal')
        || pool[0]
        || null;
}

function headerBlocks(theme) {
    return Array.isArray(theme?.header?.blocks) ? theme.header.blocks : [];
}

/** A brand block draws its face only as a text logo; picture logos never use it. */
function textBrand(block, theme) {
    return (block?.props?.mode || theme?.brand?.logo?.mode) === 'text';
}

/** Whether any logo on the page is set in the wordmark font. */
function hasTextLogo(theme) {
    const brands = headerBlocks(theme).filter((block) => block?.type === 'brand');
    if (!brands.length) return theme?.brand?.logo?.mode === 'text';
    return brands.some((block) => textBrand(block, theme));
}

/** Brand and menu block faces; the drawer face only shows once it opens. */
function firstPaintHeaderFaceIds(theme, skipRoles) {
    return headerBlocks(theme)
        .filter((block) => !(skipRoles.includes('nav') && block?.type === 'menu'))
        .filter((block) => block?.type !== 'brand' || textBrand(block, theme))
        .map((block) => block?.props?.faceId)
        .filter(Boolean);
}

/**
 * Preload tags for the fonts the first paint uses: the body, heading, nav and
 * wordmark faces plus header block faces, latin file only. Captions, video
 * chrome and other subsets load when they are needed. The wordmark and brand
 * faces are left out unless the logo is drawn as text.
 * @param {Object|Object[]} themes
 * @param {Object} [library]
 * @param {{ skipRoles?: string[] }} [options] - roles the page does not show,
 *   e.g. `['nav']` on a page without a menu
 * @returns {string}
 */
export function fontPreloadTags(themes, library, { skipRoles = [] } = {}) {
    const list = Array.isArray(themes) ? themes : [themes];
    const seen = new Set();
    const tags = [];
    list.forEach((theme) => {
        const lib = withThemeFaces(theme, library);
        const skip = hasTextLogo(theme) ? skipRoles : [...skipRoles, 'wordmark'];
        const ids = [
            ...FACE_ROLES
                .filter((role) => !role.optional && !skip.includes(role.id))
                .map((role) => theme?.fonts?.[role.id]),
            ...firstPaintHeaderFaceIds(theme, skipRoles),
        ];
        ids.forEach((id) => {
            const file = firstPaintFile(resolveFace(id, lib));
            const url = file?.url;
            if (!url || seen.has(url)) return;
            seen.add(url);
            const format = file.format || (String(url).endsWith('.woff2') ? 'woff2' : 'woff');
            const type = format === 'woff2' ? 'font/woff2' : 'font/woff';
            const href = String(url).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
            tags.push(`<link rel="preload" as="font" type="${type}" href="${href}" crossorigin />`);
        });
    });
    return tags.join('\n');
}

/**
 * Strip marketing names from the library before it rides with public content.
 * Admin UI loads full faces from /api/admin/library instead.
 * @param {Object|null} library
 * @returns {Object}
 */
export function publicLibrary(library, catalog = null) {
    const glyphs = Array.isArray(catalog?.glyphs)
        ? catalog.glyphs
        : (Array.isArray(library?.glyphs) ? library.glyphs : []);
    return {
        faces: (library?.faces || []).map((face) => ({
            id: face.id,
            family: face.family,
            url: face.url,
            format: face.format || 'woff2',
            weight: face.weight || 400,
            style: face.style || 'normal',
            variants: Array.isArray(face.variants)
                ? face.variants.map((variant) => ({
                    url: variant.url,
                    format: variant.format || 'woff2',
                    weight: variant.weight || 400,
                    style: variant.style || 'normal',
                    ...(variant.subset ? { subset: variant.subset } : null),
                    ...(variant.unicodeRange ? { unicodeRange: variant.unicodeRange } : null),
                }))
                : undefined,
            metrics: face.metrics || undefined,
            // Never expose google-import / googleFamily / label to visitors.
            source: face.url ? 'file' : face.source,
        })),
        iconSets: library?.iconSets || [],
        glyphs: glyphs.map((glyph) => ({
            id: glyph.id,
            name: glyph.name || glyph.label || glyph.id,
            url: glyph.url || null,
            builtin: glyph.builtin || null,
        })),
    };
}
